import { combineReducers } from 'redux';
import { reducer as formReducer, isInvalid } from 'redux-form';
import { SURVEY_SECTION_END } from 'sharedParts';
import surveyTakerReducer, * as fromSurveyTaker from 'taker/reducers/surveyTakerReducer';
import responseReducer, * as fromResponseReducer from 'taker/reducers/responseReducer';
import mapEditorSectionToUrl from 'config/mapEditorSectionToUrl';
import surveyEndRedirectPage from 'constants/surveyEndRedirectPage';
import imageStorageReducer, {
  IMAGE_STORAGE_STATE_PATH,
} from 'imageStorage/reducers';

import stylingReducer, {
  STYLING_STATE_PATH,
} from '../../domains/styling/reducers';
import { DUPLICATED_PREFIX } from '../../utils/generateQuestionFormName';

export default () =>
  combineReducers({
    survey: surveyTakerReducer,
    response: responseReducer,
    form: formReducer,
    [IMAGE_STORAGE_STATE_PATH]: imageStorageReducer,
    [STYLING_STATE_PATH]: stylingReducer,
  });

export const isAnyAnswerPending = state =>
  fromResponseReducer.isAnyAnswerPending(state.response);

export const getAwaitForAnswerNextButtonId = state =>
  fromResponseReducer.getAwaitForAnswerNextButtonId(state.response);

export const getSurveyResponseUuid = state =>
  fromResponseReducer.getSurveyResponseUuid(state.response);

export const getSurveyAnswers = state =>
  fromResponseReducer.getAnswers(state.response);

export const getSurveyAnswerById = (state, id) =>
  fromResponseReducer.getAnswerById(state.response, id);

export const getCurrentPage = state =>
  fromSurveyTaker.getCurrentPage(state.survey);

export const getDisplayPage = state =>
  fromSurveyTaker.getDisplayPage(state.survey);

export const isCaptchaVerified = state =>
  fromSurveyTaker.isCaptchaVerified(state.survey);

export const isCaptchaPending = state =>
  fromSurveyTaker.isCaptchaPending(state.survey);

export const getSurveyError = state => fromSurveyTaker.getError(state.survey);

export const getSurveySectionLastPageNumber = state =>
  fromSurveyTaker.getSurveySectionLastPageNumber(state.survey);

export const hasSurveyQuestionsSectionOnlyOnePage = state =>
  fromSurveyTaker.hasSurveyQuestionsSectionOnlyOnePage(state.survey);

export const getBlockList = state => fromSurveyTaker.getBlockList(state.survey);

export const getQuestionBlockList = (state) => fromSurveyTaker.getQuestionBlockList(state.survey);

export const getCurrentPageBlockList = state =>
  fromSurveyTaker.getCurrentPageBlockList(state.survey);

export const getRedirectUrl = state =>
  fromSurveyTaker.getRedirectUrl(state.survey);

export const getFirstPageNumber = state =>
  fromSurveyTaker.getFirstPageNumber(state.survey);

export const getSurveySettings = state =>
  fromSurveyTaker.getSettings(state.survey);

export const isEndEnabled = state => fromSurveyTaker.isEndEnabled(state.survey);

export const shouldRenderPoweredBy = state =>
  fromSurveyTaker.shouldRenderPoweredBy(state.survey);

export const shouldRenderCaptcha = state =>
  fromSurveyTaker.shouldRenderCaptcha(state.survey);

export const isSurveyFullWidth = state =>
  fromSurveyTaker.isSurveyFullWidth(state.survey);

export const isCaptchaEnabled = state =>
  fromSurveyTaker.isCaptchaEnabled(state.survey);

export const getLatestVisitedPageNumber = state =>
  fromSurveyTaker.getLatestVisitedPageNumber(state.survey);

export const getVisitedPages = state =>
  fromSurveyTaker.getVisitedPages(state.survey);

export const getVisitedQuestions = state =>
  fromSurveyTaker.getVisitedQuestions(state.survey);

export const getVisitedPagesLength = state =>
  fromSurveyTaker.getVisitedPagesLength(state.survey);

export const getLastPageNumber = state =>
  fromSurveyTaker.getLastPageNumber(state.survey);

export const isSurveyFirstPage = state =>
  fromSurveyTaker.isFirstPage(state.survey);

export const isSurveyLastPage = state =>
  fromSurveyTaker.isLastPage(state.survey);

// skip logic
export const isQuestionLogicValid = (state, questionId) =>
  fromSurveyTaker.isQuestionLogicValid(state.survey, questionId);

export const isQuestionLogicActionSkip = (state, questionId) =>
  fromSurveyTaker.isQuestionLogicActionSkip(state.survey, questionId);

export const getQuestionLogicRedirectBlockId = (state, questionId, answer) =>
  fromSurveyTaker.getQuestionLogicRedirectBlockId(state.survey, questionId, answer);

export const getQuestionLogicRedirectPage = (state, questionId, answer) =>
  fromSurveyTaker.getQuestionLogicRedirectPage(state.survey, questionId, answer);

export const getPendingRedirectToBlockId = (state) =>
  fromSurveyTaker.getPendingRedirectToBlockId(state.survey);
// end skip logic

// Show Logic
export const isQuestionLogicActionShow = (state, questionId) =>
  fromSurveyTaker.isQuestionLogicActionShow(state.survey, questionId);

export const getConditionalShowLogicQuestions = (state) =>
  fromSurveyTaker.getConditionalShowLogicQuestions(state.survey);

export const isConditionalShowLogicQuestion = (state, questionUuid) =>
  fromSurveyTaker.isConditionalShowLogicQuestion(state.survey, questionUuid);

export const answersMatchShowLogicConditions = (state, questionUuid) =>
  fromSurveyTaker.answersMatchShowLogicConditions(state.survey, state.response, questionUuid);

export const shouldShowQuestionBlock = (state, questionUuid) =>
  fromSurveyTaker.shouldShowQuestionBlock(state.survey, state.response, questionUuid);

export const getConditionalShowLogicQuestionVisibility = (state, questionUuid) =>
  fromSurveyTaker.getConditionalShowLogicQuestionVisibility(state.survey, questionUuid);

export const getVisibleQuestionBlocks = (state, blockList) =>
  fromSurveyTaker.getVisibleQuestionBlocks(state.survey, state.response, blockList);
// End Show Logic

export const getInvalidFormNameToScroll = state =>
  fromSurveyTaker.getInvalidFormNameToScroll(state.survey);

export const isBlockNextButton = (state, id) =>
  fromSurveyTaker.isBlockNextButton(state.survey, id);

export const isBlockNextOrSubmitButton = (state, id) =>
  fromSurveyTaker.isBlockNextOrSubmitButton(state.survey, id);

export const getLastQuestionIdOnPage = (state, page) =>
  fromSurveyTaker.getLastQuestionIdOnPage(state.survey, page);

export const getBlockPage = (state, id) =>
  fromSurveyTaker.getBlockPage(state.survey, id);

export const getCurrentSectionName = state => {
  const blockList = getCurrentPageBlockList(state);

  return blockList.length > 0 ? blockList[0].section : SURVEY_SECTION_END;
};

export const isLandingCurrentSection = state =>
  getCurrentSectionName(state) === mapEditorSectionToUrl.landing;

export const getPendingRedirectIdByNextPageNumber = (state, nextPage) => {
  const lastPageQuestionId = getLastQuestionIdOnPage(state, nextPage);
  const lastQuestionAnswer = getSurveyAnswerById(state, lastPageQuestionId);
  return getQuestionLogicRedirectBlockId(
    state,
    lastPageQuestionId,
    lastQuestionAnswer,
  );
};

export const getRedirectToBlockId = state =>
  fromSurveyTaker.getRedirectToBlockId(state.survey);

export const getSectionHeaderBackgroundImageUrl = (state, section) =>
  fromSurveyTaker.getSectionHeaderBackgroundImageUrl(state.survey, section);

export const getSectionHeaderBackgroundImageSize = (state, section) =>
  fromSurveyTaker.getSectionHeaderBackgroundImageSize(state.survey, section);

export const getSectionHeaderLogoImageUrl = (state, section) =>
  fromSurveyTaker.getSectionHeaderLogoImageUrl(state.survey, section);

export const getSectionHeaderLogoImageAlign = (state, section) =>
  fromSurveyTaker.getSectionHeaderLogoImageAlign(state.survey, section);

export const getSectionHeaderLogoWithTextAlign = (state, section) =>
  fromSurveyTaker.getSectionHeaderLogoWithTextAlign(state.survey, section);

export const getSectionHeaderHeight = (state, section) =>
  fromSurveyTaker.getSectionHeaderHeight(state.survey, section);

export const getSectionHeaderBackgroundColor = (state, section) =>
  fromSurveyTaker.getSectionHeaderBackgroundColor(state.survey, section);

export const isSectionHeaderFullWidth = (state, section) =>
  fromSurveyTaker.isSectionHeaderFullWidth(state.survey, section);

export const getSectionHeaderBackgroundCoords = (state, section) =>
  fromSurveyTaker.getSectionHeaderBackgroundCoords(state.survey, section);

export const isSectionHeaderPlaceholder = (state, section) =>
  fromSurveyTaker.isSectionHeaderPlaceholder(state.survey, section);

export const getHeaderPageBoxType = (state, sectionName) =>
  fromSurveyTaker.getHeaderPageBoxType(state.survey, sectionName);

export const isSectionHeaderContentEnabled = (state, section) =>
  fromSurveyTaker.isSectionHeaderContentEnabled(state.survey, section);

export const getSectionHeaderLogoImageLinkUrl = (state, section) =>
  fromSurveyTaker.getSectionHeaderLogoImageLinkUrl(state.survey, section);

export const getSectionHeaderLogoImageSizePreset = (state, section) =>
  fromSurveyTaker.getSectionHeaderLogoImageSizePreset(state.survey, section);

export const getSectionHeaderLogoImageSize = (state, section) =>
  fromSurveyTaker.getSectionHeaderLogoImageSize(state.survey, section);

export const getSectionHeaderTextContent = (state, section) =>
  fromSurveyTaker.getSectionHeaderTextContent(state.survey, section);

export const getSectionHeaderLayout = (state, section) =>
  fromSurveyTaker.getSectionHeaderLayout(state.survey, section);

export const getSectionHeaderClientUuid = (state, section) =>
  fromSurveyTaker.getSectionHeaderClientUuid(state.survey, section);

const getSurveySharedHeaderSection = state =>
  fromSurveyTaker.getSharedHeaderSection(state.survey);

export const getCurrentSectionHeaderBackgroundImageUrl = state =>
  getSectionHeaderBackgroundImageUrl(
    state,
    getSurveySharedHeaderSection(state) || getCurrentSectionName(state),
  );

export const getCurrentSectionHeaderBackgroundImageSize = state =>
  getSectionHeaderBackgroundImageSize(
    state,
    getSurveySharedHeaderSection(state) || getCurrentSectionName(state),
  );

export const getCurrentSectionHeaderLogoImageUrl = state =>
  getSectionHeaderLogoImageUrl(
    state,
    getSurveySharedHeaderSection(state) || getCurrentSectionName(state),
  );

export const getCurrentSectionHeaderLogoImageAlign = state =>
  getSectionHeaderLogoImageAlign(
    state,
    getSurveySharedHeaderSection(state) || getCurrentSectionName(state),
  );

export const getCurrentSectionHeaderLogoWithTextAlign = state =>
  getSectionHeaderLogoWithTextAlign(
    state,
    getSurveySharedHeaderSection(state) || getCurrentSectionName(state),
  );

export const getCurrentSectionHeaderHeight = state =>
  getSectionHeaderHeight(
    state,
    getSurveySharedHeaderSection(state) || getCurrentSectionName(state),
  );

export const getCurrentSectionHeaderBackgroundColor = state =>
  getSectionHeaderBackgroundColor(
    state,
    getSurveySharedHeaderSection(state) || getCurrentSectionName(state),
  );

export const isCurrentSectionHeaderFullWidth = state =>
  isSectionHeaderFullWidth(
    state,
    getSurveySharedHeaderSection(state) || getCurrentSectionName(state),
  );

export const getCurrentSectionHeaderBackgroundCoords = state =>
  getSectionHeaderBackgroundCoords(
    state,
    getSurveySharedHeaderSection(state) || getCurrentSectionName(state),
  );

export const isCurrentSectionHeaderPlaceholder = state =>
  isSectionHeaderPlaceholder(
    state,
    getSurveySharedHeaderSection(state) || getCurrentSectionName(state),
  );

export const getCurrentSectionHeaderPageBoxType = state =>
  getHeaderPageBoxType(
    state,
    getSurveySharedHeaderSection(state) || getCurrentSectionName(state),
  );

export const isCurrentSectionHeaderContentEnabled = state =>
  isSectionHeaderContentEnabled(
    state,
    getSurveySharedHeaderSection(state) || getCurrentSectionName(state),
  );

export const getCurrentSectionHeaderLogoImageLinkUrl = state =>
  getSectionHeaderLogoImageLinkUrl(
    state,
    getSurveySharedHeaderSection(state) || getCurrentSectionName(state),
  );

export const getCurrentSectionHeaderLogoImageSizePreset = state =>
  getSectionHeaderLogoImageSizePreset(
    state,
    getSurveySharedHeaderSection(state) || getCurrentSectionName(state),
  );

export const getCurrentSectionHeaderLogoImageSize = state =>
  getSectionHeaderLogoImageSize(
    state,
    getSurveySharedHeaderSection(state) || getCurrentSectionName(state),
  );

export const getCurrentSectionHeaderLayout = state =>
  getSectionHeaderLayout(
    state,
    getSurveySharedHeaderSection(state) || getCurrentSectionName(state),
  );

export const getCurrentSectionHeaderTextContent = state =>
  getSectionHeaderTextContent(
    state,
    getSurveySharedHeaderSection(state) || getCurrentSectionName(state),
  );

export const getCurrentSectionHeaderClientUuid = state =>
  getSectionHeaderClientUuid(
    state,
    getSurveySharedHeaderSection(state) || getCurrentSectionName(state),
  );

export const getCurrentSectionHeaderEnabled = state =>
  fromSurveyTaker.getHeaderEnabled(state.survey, getCurrentSectionName(state));

export const getQuestionNumberingEnabled = state =>
  fromSurveyTaker.getQuestionNumberingEnabled(state.survey);

export const getNextPageNumber = (
  state,
  currentPage = getCurrentPage(state),
) => {
  const lastPageQuestionId = getLastQuestionIdOnPage(state, currentPage);
  const lastQuestionAnswer = getSurveyAnswerById(state, lastPageQuestionId);
  const logicNextPage = getQuestionLogicRedirectPage(
    state,
    lastPageQuestionId,
    lastQuestionAnswer,
  );

  return logicNextPage || currentPage + 1;
};

export const getPageToResume = (state, lastClickedNextButtonId) => {
  const lastClickedNextButtonPage = getBlockPage(
    state,
    lastClickedNextButtonId,
  );
  const lastPageQuestionId = getLastQuestionIdOnPage(
    state,
    lastClickedNextButtonPage,
  );
  if (isQuestionLogicValid(state, lastPageQuestionId)) {
    const lastQuestionAnswer = getSurveyAnswerById(state, lastPageQuestionId);
    const redirectPage = getQuestionLogicRedirectPage(
      state,
      lastPageQuestionId,
      lastQuestionAnswer,
    );
    if (redirectPage) {
      return redirectPage;
    }
  }
  return Number.isInteger(lastClickedNextButtonPage)
    ? lastClickedNextButtonPage + 1
    : getFirstPageNumber(state);
};

export const getFormNames = state => Object.keys(state.form) || [];

export const getQuestionsFormNames = state =>
  getFormNames(state).filter(
    /* exclude duplicated forms (needed for preview generation) from validation check @dkaminskiy */
    name =>
      name.includes('question-form-') && !name.includes(DUPLICATED_PREFIX),
  );

export const getInvalidQuestionFormNames = state =>
  getQuestionsFormNames(state).filter(name => isInvalid(name)(state));

export const isAllPageAnswersValid = state =>
  !getInvalidQuestionFormNames(state).length;

export const isAllowedToNextPage = state =>
  isAllPageAnswersValid(state) && !isAnyAnswerPending(state);

export const getFirstInvalidFormName = state => {
  const invalidForms = getInvalidQuestionFormNames(state);
  for (const { clientUuid } of getCurrentPageBlockList(state)) {
    const invalidForm = invalidForms.find(element =>
      element.includes(clientUuid),
    );
    if (invalidForm) return invalidForm;
  }
  return null;
};

export const getBlockListBySectionName = (state, sectionName) =>
  fromSurveyTaker
    .getBlockList(state.survey)
    .filter(({ section }) => section === sectionName);

export const isSurveyNextPageRedirect = state => {
  const nextPage = getNextPageNumber(state);
  const lastPage = getLastPageNumber(state);

  return (
    nextPage === surveyEndRedirectPage ||
    (nextPage > lastPage && !isEndEnabled(state))
  );
};
export const isEndEmpty = state =>
  getBlockListBySectionName(state, SURVEY_SECTION_END).length === 0;

export const isSurveyCompleted = state =>
  fromSurveyTaker.isSurveyCompleted(state.survey);

export const isSurveyBackButtonEnabled = state =>
  fromSurveyTaker.isBackButtonEnabled(state.survey);
